import type { AdaptationListAsset } from "@/features/collection_home/types";
import { decryptData } from "@/features/cryptography";
import { GetAdaptationActionResponse, supabaseApi } from "@/store/services/supabase";
import { dispatchWithRetries } from "@/store/services/supabase/utils";
import { PortfolioExportJobSchema } from "@/tools/aggregate/portfolio-export/types";
import { AdaptationType } from "@/types/adaptation";
import { hexToArrayBuffer } from "@/utils/crypto";
import { Effect, pipe } from "effect";
import { ResponseSuccessWithMetadata, type ResultsMode, type Scenario } from "../schema";
import type { DBConfig } from "../utils/indexeddb";
import { IndexedDb } from "../utils/indexeddb";
import { lineReducer } from "../utils/line_reducer";
import { streamResults } from "../utils/stream";
import { hasAdaptationIdentity, type CachedAdaptation } from "./AdaptationCacheSchema";
import type { OnProgressCallback } from "./AssetDataLoader";
import { adaptationActionCount, isAdaptationCacheComplete, type AdaptationRun } from "./cacheValidity";
import { ADAPTATIONS_IDB_STORE_NAME, createPortfolioCacheConfig } from "./portfolioCache";

const BATCH_SIZE = 100;
const CONCURRENCY_LIMIT = 3;
const RUN_CONCURRENCY_LIMIT = 6;
const MAX_RETRIES = 10;
const RETRY_TIMEOUT = 3000;

const DEFAULT_SCENARIO: Scenario = "rcp85";

/**
 * A pathway action the caller already holds decrypted, used when loading a
 * single point's pathways rather than the whole portfolio.
 */
export interface AdaptationActionInput {
  id: string;
  portfolio_asset_id: string;
  adaptation_type: AdaptationType;
  data: ResponseSuccessWithMetadata["metadata"];
}

type AnalysisMetadata = AdaptationActionInput;

const extractAdaptationScenarioResults = (
  full: ResponseSuccessWithMetadata,
  scenario: Scenario
): AdaptationListAsset["result"]["results"] => {
  const scenarioData = full.result.results[scenario];
  if (!scenarioData) return {};
  return {
    [scenario]: {
      damage: scenarioData.damage,
      failure: scenarioData.failure,
      productivity_loss: scenarioData.productivity_loss,
    },
  };
};

const isValidResult = (full: ResponseSuccessWithMetadata): boolean =>
  full?.result?.metadata != null && full.result.results?.[DEFAULT_SCENARIO]?.damage != null;

/** Build a list entry from a cached record. Callers validate `full` first. */
const transformCachedAdaptation = (cached: CachedAdaptation, scenario: Scenario): AdaptationListAsset | null => {
  const data = cached.full.metadata;
  if (data == null) return null;

  return {
    uuid: cached.full.item_id,
    adaptation_type: cached.adaptation_type,
    portfolio_asset_id: cached.portfolio_asset_id,
    data,
    result: {
      results: extractAdaptationScenarioResults(cached.full, scenario),
    },
  };
};

export class AdaptationsLoader {
  private config: DBConfig;
  private schema: PortfolioExportJobSchema;
  private resultsMode: ResultsMode;
  private runs: readonly AdaptationRun[] = [];
  private allAnalysisMetadataPromise: Promise<AnalysisMetadata[]> | null = null;
  private analysisMetadataByIdPromise: Promise<ReadonlyMap<string, AnalysisMetadata>> | null = null;

  constructor(schema: PortfolioExportJobSchema, resultsMode: ResultsMode = "comprehensive") {
    this.schema = schema;
    this.resultsMode = resultsMode;
    this.config = createPortfolioCacheConfig(schema.id, { store: ADAPTATIONS_IDB_STORE_NAME, resultsMode });
  }

  async loadForAdaptationPathwayActions(actions: AdaptationActionInput[]): Promise<CachedAdaptation[]> {
    const actionIds = actions.map((action) => action.id);
    const actionsById = new Map(actions.map((action) => [action.id, action] as const));

    const cachedActions = await IndexedDb.getItemsByIds<CachedAdaptation>(this.config, actionIds);
    const cachedIds = new Set(cachedActions.map((a) => a.id));

    const actionIdsToFetch = actionIds.filter((id) => !cachedIds.has(id));

    if (actionIdsToFetch.length > 0) {
      const fetched = await this.fetchAndCacheWithMetadata(actionIdsToFetch, actionsById);
      cachedActions.push(...fetched);
    }

    // Validate already-cached entries too; recover corrupt ones, fail-fast on any residual corruption.
    const { valid, corruptIds } = this.validateCached(cachedActions);
    if (corruptIds.length > 0) {
      const { recovered, stillCorrupt } = await this.recoverCorruptAdaptations(corruptIds, actionsById);
      valid.push(...recovered);
      if (stillCorrupt.length > 0) {
        throw new Error(`[AdaptationsLoader] ${stillCorrupt.length} pathway actions still corrupt after recovery`);
      }
    }

    return valid;
  }

  /**
   * Partition cached entries into valid and corrupt.
   */
  private validateCached(cached: CachedAdaptation[]): { valid: CachedAdaptation[]; corruptIds: string[] } {
    const valid: CachedAdaptation[] = [];
    const corruptIds: string[] = [];
    cached.forEach((c) => {
      if (isValidResult(c.full)) valid.push(c);
      else corruptIds.push(c.id);
    });
    return { valid, corruptIds };
  }

  private async recoverCorruptAdaptations(
    corruptIds: string[],
    actionsById?: ReadonlyMap<string, AdaptationActionInput>,
    onRecoveryProgress?: OnProgressCallback
  ): Promise<{ recovered: CachedAdaptation[]; stillCorrupt: string[] }> {
    console.warn(`[AdaptationsLoader] Recovering ${corruptIds.length} corrupt cache adaptations`);

    const total = corruptIds.length;
    onRecoveryProgress?.({ downloaded: 0, processed: 0, total });

    await Promise.all(corruptIds.map((id) => IndexedDb.deleteItem(this.config, id)));

    if (actionsById) {
      await this.fetchAndCacheWithMetadata(corruptIds, actionsById);
      onRecoveryProgress?.({ downloaded: total, processed: total, total });
    } else {
      let downloaded = 0;
      await this.fetchAndCacheFull(corruptIds, (count) => {
        downloaded += count;
        const processed = Math.min(downloaded, total);
        onRecoveryProgress?.({ downloaded, processed, total });
      });
    }

    const refetched = await IndexedDb.getItemsByIds<CachedAdaptation>(this.config, corruptIds);
    const { valid, corruptIds: stillCorrupt } = this.validateCached(refetched);

    return { recovered: valid, stillCorrupt };
  }

  private async fetchAndCacheWithMetadata(
    ids: string[],
    actionsById: ReadonlyMap<string, AdaptationActionInput>
  ): Promise<CachedAdaptation[]> {
    const res = await streamResults(ids, []);
    if (!res.body) {
      throw new Error("No response body received from stream");
    }

    const results = await processStreamToResults(res.body, (itemId) => actionsById.get(itemId)?.data);
    const toCache = toCachedAdaptations(results, actionsById);

    await IndexedDb.upsertItems(this.config, toCache);
    return toCache;
  }

  /**
   * Private: Pull encrypted pathway actions from Supabase and decrypt
   */
  private async pullAnalysisMetadata(
    adaptationId: string,
    previousBatchEndId: string | undefined,
    limit: number
  ): Promise<{ metadata: AnalysisMetadata[]; batchEndId: string | undefined }> {
    const action = supabaseApi.endpoints.getAllPortfolioAdaptationPathwayActions.initiate({
      portfolioId: this.schema.id,
      adaptationId,
      previousBatchEndId,
      limit,
    });

    const data = await dispatchWithRetries<GetAdaptationActionResponse[]>({
      action,
      maxRetries: MAX_RETRIES,
      timeout: RETRY_TIMEOUT,
    });

    const metadata = await Promise.all(
      data.map(async (analysis) => {
        if (!analysis.data) {
          throw new Error("Missing encrypted asset data");
        }
        const decryptedData = await decryptData(this.schema.secret, hexToArrayBuffer(analysis.data));
        const decryptedId = await decryptData(this.schema.secret, hexToArrayBuffer(analysis.encrypted_id));

        return {
          id: decryptedId,
          portfolio_asset_id: analysis.portfolio_asset_id,
          adaptation_type: analysis.adaptation_type ?? null,
          data: JSON.parse(decryptedData) as ResponseSuccessWithMetadata["metadata"],
        };
      })
    );

    const batchEndId = data.length > 0 ? data[data.length - 1].id : undefined;
    return { metadata, batchEndId };
  }

  /**
   * Private: Pull every pathway action for the loaded runs, memoised per loader
   * instance.
   *
   * A run pages by cursor, so its own batches have to stay serial, but the runs
   * are independent of each other. The whole pull is a hard serial prefix before
   * a single result can start downloading, so this latency is paid before
   * anything renders — running the runs together is most of what can be saved.
   * `onLoaded` reports the running total; because the result is memoised it only
   * fires for the first caller, which is the one that actually waits.
   */
  private async getAllAnalysisMetadata(onLoaded?: (loaded: number) => void): Promise<AnalysisMetadata[]> {
    if (!this.allAnalysisMetadataPromise) {
      this.allAnalysisMetadataPromise = (async () => {
        let loaded = 0;

        const pullRun = (run: AdaptationRun) =>
          Effect.tryPromise({
            try: async () => {
              const actions: AnalysisMetadata[] = [];
              let previousBatchEndId: string | undefined;
              let hasMore = true;

              while (hasMore) {
                const { metadata, batchEndId } = await this.pullAnalysisMetadata(
                  run.id,
                  previousBatchEndId,
                  BATCH_SIZE
                );
                actions.push(...metadata);
                previousBatchEndId = batchEndId;
                loaded += metadata.length;
                onLoaded?.(loaded);

                //if final chunk length is same as batch, one more req will fire
                hasMore = metadata.length === BATCH_SIZE;
              }

              return actions;
            },
            // Keep the underlying Supabase error rather than wrapping it in a
            // defect, so the failure that reaches Sentry is the real one.
            catch: (error) => (error instanceof Error ? error : new Error(String(error))),
          });

        const perRun = await Effect.runPromise(
          Effect.forEach(this.runs, pullRun, { concurrency: RUN_CONCURRENCY_LIMIT })
        );
        return perRun.flat();
      })();
    }
    return this.allAnalysisMetadataPromise;
  }

  /** Private: the pathway actions indexed by analysis id, built once per loader. */
  private async getAnalysisMetadataById(): Promise<ReadonlyMap<string, AnalysisMetadata>> {
    if (!this.analysisMetadataByIdPromise) {
      this.analysisMetadataByIdPromise = this.getAllAnalysisMetadata().then(
        (all) => new Map(all.map((a) => [a.id, a] as const))
      );
    }
    return this.analysisMetadataByIdPromise;
  }

  /** How many records the adaptation store holds once every run is cached. */
  private expectedActionCount(): number {
    return adaptationActionCount({ schema: this.schema, runs: this.runs });
  }

  /**
   * Private: How many adaptations are cached, without materialising their ids.
   */
  private async getCachedAdaptationCount(): Promise<number> {
    try {
      const { count } = await IndexedDb.getCount(this.config);
      return count;
    } catch {
      return 0;
    }
  }

  /**
   * Private: Get the ids of all cached adaptations without loading their values
   */
  private async getCachedAdaptationIds(): Promise<string[]> {
    try {
      return (await IndexedDb.getAllKeys(this.config)) as string[];
    } catch {
      return [];
    }
  }

  /**
   * Fills the cache so every pathway action of the loaded runs is present.
   *
   * Returns the actions it had to pull, or `null` when the store already held
   * them all and no pull happened — which is the whole point of the count check.
   */
  private async ensureCacheComplete(onProgress?: OnProgressCallback): Promise<AnalysisMetadata[] | null> {
    const total = this.expectedActionCount();

    // Only the diff against the count
    const cachedCount = await this.getCachedAdaptationCount();

    if (isAdaptationCacheComplete({ schema: this.schema, runs: this.runs, cachedCount })) {
      console.log(`[AdaptationsLoader] cache complete (${cachedCount} adaptations), skipping pathway action fetch`);
      return null;
    }

    const cachedIds = new Set(await this.getCachedAdaptationIds());

    const allAnalysisMetadata = await this.getAllAnalysisMetadata((prepared) => {
      onProgress?.({ phase: "preparing", downloaded: 0, processed: prepared, total });
    });
    const idsToFetch = allAnalysisMetadata.map((a) => a.id).filter((id) => !cachedIds.has(id));

    console.log(`[AdaptationsLoader] ${cachedIds.size}/${total} adaptations cached, fetching ${idsToFetch.length}`);

    if (idsToFetch.length > 0) {
      let downloaded = 0;
      await this.fetchAndCacheFull(idsToFetch, (count) => {
        downloaded += count;
        onProgress?.({
          phase: "downloading",
          downloaded,
          processed: Math.min(cachedIds.size + downloaded, total),
          total,
        });
      });
    }

    return allAnalysisMetadata;
  }

  /**
   * Private: Fill the cache, then walk the store once, handing each batch's
   * requested pathways to `onRecords`. Corrupt records are re-fetched and handed
   * over the same way; anything still corrupt fails the load.
   *
   * Callers must do their work inside `onRecords`: only one batch of full
   * results is in memory at a time, and the whole store is several GB. The
   * second argument is how many records the batch read, which is more than it
   * is handed.
   */
  private async scanCachedAdaptations(
    requestedTypes: ReadonlySet<AdaptationType>,
    {
      validate,
      onProgress,
      onCacheReady,
      onRecoveryProgress,
      onRecords,
    }: {
      validate: boolean;
      onProgress?: OnProgressCallback;
      onCacheReady?: () => void;
      onRecoveryProgress?: OnProgressCallback;
      onRecords: (records: CachedAdaptation[], scannedCount: number) => Promise<void>;
    }
  ): Promise<void> {
    const pulledPathwayActions = await this.ensureCacheComplete(onProgress);
    onCacheReady?.();

    // Only a cold load has the pathway action list
    // warm load gets items from indexdb
    const existingActionIds = pulledPathwayActions ? new Set(pulledPathwayActions.map((action) => action.id)) : null;
    const corruptRecordIds: string[] = [];

    await IndexedDb.getAllItemsBatched<CachedAdaptation>(this.config, async (cachedBatch) => {
      const existingRecords = existingActionIds
        ? cachedBatch.filter((record) => existingActionIds.has(record.id))
        : cachedBatch;
      const identifiedRecords = await this.identifyBatch(existingRecords);

      let recordsToDeliver = identifiedRecords.filter((record) => requestedTypes.has(record.adaptation_type));

      if (validate) {
        const { valid, corruptIds: batchCorruptIds } = this.validateCached(recordsToDeliver);
        recordsToDeliver = valid;
        corruptRecordIds.push(...batchCorruptIds);
      }

      await onRecords(recordsToDeliver, identifiedRecords.length);
    });

    if (corruptRecordIds.length === 0) return;

    const { recovered: recoveredRecords, stillCorrupt: stillCorruptIds } = await this.recoverCorruptAdaptations(
      corruptRecordIds,
      undefined,
      onRecoveryProgress
    );
    // Recovery must not sneak in a pathway the scan deliberately skipped.
    await onRecords(
      recoveredRecords.filter((record) => requestedTypes.has(record.adaptation_type)),
      0
    );

    if (stillCorruptIds.length > 0) {
      throw new Error(`[AdaptationsLoader] ${stillCorruptIds.length} adaptations still corrupt after recovery`);
    }
  }

  /**
   * Load the map-wide adaptation list.
   */
  async loadForMapView(
    runs: readonly AdaptationRun[],
    scenario: Scenario,
    {
      listTypes,
      onProgress,
      onRecoveryProgress,
    }: {
      listTypes: readonly AdaptationType[];
      onProgress?: OnProgressCallback;
      onRecoveryProgress?: OnProgressCallback;
    }
  ): Promise<AdaptationListAsset[]> {
    this.runs = runs;

    const total = this.expectedActionCount();

    const lightweightResults: AdaptationListAsset[] = [];
    let scanned = 0;

    await this.scanCachedAdaptations(new Set(listTypes), {
      validate: true,
      onProgress,
      onRecoveryProgress,
      onRecords: async (records, scannedInBatch) => {
        scanned += scannedInBatch;

        records.forEach((cached) => {
          const listAsset = transformCachedAdaptation(cached, scenario);
          if (listAsset) lightweightResults.push(listAsset);
        });

        // On a warm cache this pass is the whole load, so it drives the progress
        onProgress?.({
          phase: "transforming",
          downloaded: 0,
          processed: Math.min(scanned, total),
          total,
        });
      },
    });

    onProgress?.({ phase: "transforming", downloaded: 0, processed: total, total });

    return lightweightResults;
  }

  /**
   * Hand the requested pathways' results to `onBatch`, one store batch at a time.
   *
   * One store holds every pathway, so each batch is split by `adaptation_type`:
   * rows arrive interleaved, and each `onBatch` call writes one pathway's file.
   */
  async loadForDownload(
    runs: readonly AdaptationRun[],
    adaptationTypes: readonly AdaptationType[],
    {
      onBatch,
      onProgress,
    }: {
      onBatch: (results: ResponseSuccessWithMetadata[], adaptationType: AdaptationType) => Promise<void>;
      onProgress?: OnProgressCallback;
    }
  ): Promise<void> {
    const requestedTypes = new Set(adaptationTypes);
    if (requestedTypes.size === 0) return;

    this.runs = runs;

    const total = this.expectedActionCount();

    const dispatch = async (records: CachedAdaptation[]) => {
      const byType = new Map<AdaptationType, ResponseSuccessWithMetadata[]>();
      records.forEach((cached) => {
        const bucket = byType.get(cached.adaptation_type);
        if (bucket) bucket.push(cached.full);
        else byType.set(cached.adaptation_type, [cached.full]);
      });

      for (const [adaptationType, results] of byType) {
        await onBatch(results, adaptationType);
      }
    };

    await this.scanCachedAdaptations(requestedTypes, {
      // Validation wants an rcp85 damage figure, which simple results never carry.
      validate: this.resultsMode !== "simple",
      onProgress,
      // A warm cache downloads nothing, so mark it done here or it never completes.
      onCacheReady: () => onProgress?.({ phase: "transforming", downloaded: total, processed: 0, total }),
      onRecords: dispatch,
    });
  }

  /**
   * Private: Return a batch with its adaptation identity, re-storing any record
   * that predates those fields.
   *
   * The rewrite costs the one pathway-action pull the count check exists to
   * avoid, but only until the store has been walked once — after that a warm
   * load answers from the cache alone. Records with no matching action are
   * dropped rather than rewritten; the action they came from no longer exists.
   */
  private async identifyBatch(batch: CachedAdaptation[]): Promise<CachedAdaptation[]> {
    if (batch.every(hasAdaptationIdentity)) return batch;

    const metadataById = await this.getAnalysisMetadataById();

    const backfilled: CachedAdaptation[] = [];
    const identified = batch.reduce<CachedAdaptation[]>((acc, record) => {
      if (hasAdaptationIdentity(record)) {
        acc.push(record);
        return acc;
      }

      const metadata = metadataById.get(record.id);
      if (!metadata) return acc;

      const rewritten = {
        ...record,
        portfolio_asset_id: metadata.portfolio_asset_id,
        adaptation_type: metadata.adaptation_type,
      };
      backfilled.push(rewritten);
      acc.push(rewritten);
      return acc;
    }, []);

    await IndexedDb.upsertItems(this.config, backfilled);

    return identified;
  }

  async loadForScenario(
    assetIds: string[],
    scenario: Scenario,
    onProgress?: OnProgressCallback
  ): Promise<{ uuid: string; result: AdaptationListAsset["result"] }[]> {
    const total = assetIds.length;
    const patches: { uuid: string; result: AdaptationListAsset["result"] }[] = [];

    // load cached scenario of supplied assetIds (only baseline/adapted required for map view )
    for (let i = 0; i < assetIds.length; i += BATCH_SIZE) {
      const chunk = assetIds.slice(i, i + BATCH_SIZE);
      const cached = await IndexedDb.getItemsByIds<CachedAdaptation>(this.config, chunk);
      cached.forEach((c) => {
        patches.push({
          uuid: c.id,
          result: { results: extractAdaptationScenarioResults(c.full, scenario) },
        });
      });
      onProgress?.({ downloaded: patches.length, processed: Math.min(i + chunk.length, total), total });
    }

    onProgress?.({ downloaded: patches.length, processed: total, total });

    return patches;
  }

  private async fetchAndCacheFull(actionIds: string[], onDownloaded?: (count: number) => void): Promise<void> {
    const metadataById = await this.getAnalysisMetadataById();

    const batches: string[][] = [];
    for (let i = 0; i < actionIds.length; i += BATCH_SIZE) {
      batches.push(actionIds.slice(i, i + BATCH_SIZE));
    }

    const fetchBatch = (batchIds: string[]) =>
      pipe(
        Effect.promise(async () => {
          const fullResults = await this.fetchFullResults(batchIds);
          const toCache = toCachedAdaptations(fullResults, metadataById);

          await IndexedDb.upsertItems(this.config, toCache);
          onDownloaded?.(batchIds.length);
        })
      );

    const batchEffects = Effect.forEach(batches, fetchBatch, { concurrency: CONCURRENCY_LIMIT, discard: true });
    await Effect.runPromise(batchEffects);
  }

  /**
   * Private: Fetch full results from API
   */
  private async fetchFullResults(assetIds: string[]): Promise<ResponseSuccessWithMetadata[]> {
    const metadataById = await this.getAnalysisMetadataById();

    // Stream results from facades
    const response = await streamResults(assetIds, this.schema.stamps, MAX_RETRIES, RETRY_TIMEOUT, this.resultsMode);

    if (!response.body) {
      throw new Error("No response body received from stream");
    }

    return processStreamToResults(response.body, (itemId) => metadataById.get(itemId)?.data);
  }
}

/** Pair each result with the action it came from, ready to store. */
const toCachedAdaptations = (
  results: ResponseSuccessWithMetadata[],
  actionsById: ReadonlyMap<string, AdaptationActionInput>
): CachedAdaptation[] =>
  results.reduce<CachedAdaptation[]>((acc, result) => {
    const action = actionsById.get(result.item_id);
    if (!action) return acc;
    acc.push({
      id: result.item_id,
      full: result,
      timestamp: Date.now(),
      portfolio_asset_id: action.portfolio_asset_id,
      adaptation_type: action.adaptation_type,
    });
    return acc;
  }, []);

/**
 * Process stream of results from facades API
 */
const processStreamToResults = async (
  body: ReadableStream<Uint8Array>,
  metadataFor: (itemId: string) => ResponseSuccessWithMetadata["metadata"] | undefined
): Promise<ResponseSuccessWithMetadata[]> => {
  const results: ResponseSuccessWithMetadata[] = [];
  const reader = body
    .pipeThrough(new TextDecoderStream() as unknown as ReadableWritablePair<string, Uint8Array>)
    .getReader();

  let previousLine = "";
  let chunk = await reader.read();

  while (!chunk.done) {
    const data = chunk.value
      .split("\n")
      .map((line) => (line.startsWith("\x1E") ? line.slice(1) : line))
      .reduce(lineReducer, {
        previousLine,
        successes: [],
        errors: [],
      });

    results.push(
      ...data.successes.map((result) => {
        const metadata = metadataFor(result.item_id);
        if (!metadata) {
          throw new Error("Analysis metadata not found for result");
        }
        return { ...result, metadata };
      })
    );

    previousLine = data.previousLine;
    chunk = await reader.read();
  }

  if (previousLine) {
    try {
      const lastResult = JSON.parse(previousLine);
      const metadata = metadataFor(lastResult.item_id);
      if (metadata) {
        results.push({ ...lastResult, metadata });
      }
    } catch {
      console.warn("Could not parse last line fragment:", previousLine);
    }
  }

  return results;
};
